import React, { useState, useRef } from 'react';
import {
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  useGridApiContext,
  useGridSelector,
  gridDensitySelector,
  ExportCsv,
  ExportPrint,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckIcon from '@mui/icons-material/Check';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import IosShareIcon from '@mui/icons-material/IosShare';

const DENSITY_OPTIONS = [
  { label: 'Compact', value: 'compact', icon: <ViewHeadlineIcon fontSize="small" /> },
  { label: 'Standard', value: 'standard', icon: <TableRowsIcon fontSize="small" /> },
  { label: 'Comfortable', value: 'comfortable', icon: <ViewStreamIcon fontSize="small" /> },
];

const DefaultToolbar = ({ children, reportHeader, reportSubheader1, reportSubheader2 }) => {
  const apiRef = useGridApiContext();
  const density = useGridSelector(apiRef, gridDensitySelector);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuTriggerRef = useRef(null);

  const handleSelect = (value) => {
    apiRef.current.setDensity(value);
    setOpen(false);
  };

  return (
    <Toolbar>
      {reportHeader && (
        <>
          <Box sx={{ flexGrow: 1, minWidth: 0, py: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>{reportHeader}</Typography>
            {reportSubheader1 && <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }} noWrap>{reportSubheader1}</Typography>}
            {reportSubheader2 && <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }} noWrap component="div">{reportSubheader2}</Typography>}
          </Box>
          <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
        </>
      )}
      <Tooltip title="Columns">
        <ColumnsPanelTrigger render={<ToolbarButton title="Columns" />}>
          <ViewColumnIcon fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>
      <Tooltip title="Filters">
        <FilterPanelTrigger render={<ToolbarButton title="Filters" />}>
          <FilterListIcon fontSize="small" />
        </FilterPanelTrigger>
      </Tooltip>
      <Tooltip title="Density">
        <ToolbarButton
          ref={triggerRef}
          onClick={() => setOpen(true)}
          // title="Density"
        >
          <TableRowsIcon fontSize="small" />
        </ToolbarButton>
      </Tooltip>
      <Menu
        anchorEl={triggerRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {DENSITY_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} onClick={() => handleSelect(opt.value)}>
            <ListItemIcon>
              {opt.icon}
            </ListItemIcon>
            <ListItemText>{opt.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <Tooltip title="Export">
        <ToolbarButton
          ref={exportMenuTriggerRef}
          id="export-menu-trigger"
          aria-controls="export-menu"
          aria-haspopup="true"
          aria-expanded={exportMenuOpen ? 'true' : undefined}
          onClick={() => setExportMenuOpen(true)}
        >
          <IosShareIcon fontSize="small" />
        </ToolbarButton>
      </Tooltip>

      <Menu
        id="export-menu"
        anchorEl={exportMenuTriggerRef.current}
        open={exportMenuOpen}
        onClose={() => setExportMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: {
            'aria-labelledby': 'export-menu-trigger',
          },
        }}
      >
        <ExportPrint render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
          Print
        </ExportPrint>
        <ExportCsv render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
          Download as CSV
        </ExportCsv>
      </Menu>
      {/* Render any children passed as extra toolbar elements */}
      {children && <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />}
      {children}
    </Toolbar>
  );
};

export default DefaultToolbar;
